import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, AlertTriangle, Phone, Users, Heart, BookOpen } from "lucide-react";

const Safeguarding = () => {
  return (
    <div className="min-h-screen bg-[#FFF9F5]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Link to="/" className="text-slate-600 hover:text-slate-800">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-xl font-bold text-[#6B4D8A]" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Safeguarding Policy
          </h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Nunito, sans-serif' }}>Safeguarding Policy</h1>
              <p className="text-slate-500">Keeping children safe is everyone's responsibility</p>
            </div>
          </div>

          <div className="prose prose-slate max-w-none">
            <div className="p-6 bg-pink-50 border border-pink-200 rounded-xl mb-8">
              <p className="text-pink-800 font-medium">
                KiddiPlay Club is committed to safeguarding and promoting the welfare of all children. We believe every child deserves to be safe, happy, and protected from harm.
              </p>
            </div>

            <section className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800 mb-4">
                <Heart className="w-5 h-5 text-pink-500" />
                Our Commitment
              </h2>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>We take all safeguarding concerns seriously and act promptly</li>
                <li>We use technology to detect and prevent potentially harmful behaviour</li>
                <li>We work with law enforcement when required by law</li>
                <li>We provide clear guidance to help parents keep their children safe</li>
                <li>We continuously review and improve our safeguarding measures</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800 mb-4">
                <Users className="w-5 h-5 text-pink-500" />
                Adult-to-Child Ratios
              </h2>
              <p className="text-slate-600 mb-4">
                We recommend following Ofsted's guidelines for adult-to-child ratios during playdates:
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="p-4 bg-slate-50 rounded-xl text-center">
                  <p className="text-3xl font-bold text-pink-500">1:3</p>
                  <p className="text-slate-600">Under 2 years</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl text-center">
                  <p className="text-3xl font-bold text-pink-500">1:4</p>
                  <p className="text-slate-600">Ages 2-3</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl text-center">
                  <p className="text-3xl font-bold text-pink-500">1:8</p>
                  <p className="text-slate-600">Ages 3+</p>
                </div>
              </div>
              <p className="text-slate-500 text-sm mt-4">
                * These are minimum recommendations. Always use your judgement based on the children's needs and the environment.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800 mb-4">
                <BookOpen className="w-5 h-5 text-pink-500" />
                Safety Tips for Families
              </h2>
              <div className="space-y-4">
                <div className="p-4 border-l-4 border-pink-400 bg-pink-50">
                  <h3 className="font-semibold text-slate-800">Before Meeting</h3>
                  <ul className="text-slate-600 mt-2 space-y-1">
                    <li>• Chat through the app first - don't share personal contact details too soon</li>
                    <li>• Video call if possible before meeting in person</li>
                    <li>• Tell someone where you're going and when you'll be back</li>
                  </ul>
                </div>
                <div className="p-4 border-l-4 border-pink-400 bg-pink-50">
                  <h3 className="font-semibold text-slate-800">First Meetups</h3>
                  <ul className="text-slate-600 mt-2 space-y-1">
                    <li>• Meet in a public place (park, soft play, café)</li>
                    <li>• Keep the first meetup short (1-2 hours)</li>
                    <li>• Trust your instincts - it's okay to leave if something feels wrong</li>
                  </ul>
                </div>
                <div className="p-4 border-l-4 border-pink-400 bg-pink-50">
                  <h3 className="font-semibold text-slate-800">During Playdates</h3>
                  <ul className="text-slate-600 mt-2 space-y-1">
                    <li>• Always supervise children - never leave them unsupervised with someone you've just met</li>
                    <li>• Know the venue's emergency procedures</li>
                    <li>• Keep your phone charged and accessible</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800 mb-4">
                <AlertTriangle className="w-5 h-5 text-pink-500" />
                How to Report a Concern
              </h2>
              <p className="text-slate-600 mb-4">
                If you have any safeguarding concerns about a child or an adult on our platform:
              </p>
              <ol className="list-decimal pl-6 text-slate-600 space-y-2">
                <li><strong>In-App:</strong> Use the "Report" button on any profile or message</li>
                <li><strong>Email:</strong> Contact <a href="mailto:safeguarding@kiddiplayclub.com" className="text-pink-600 underline">safeguarding@kiddiplayclub.com</a></li>
                <li><strong>Urgent:</strong> If a child is in immediate danger, call 999</li>
              </ol>
            </section>

            <section className="p-6 bg-rose-50 border border-rose-200 rounded-xl">
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-800 mb-4">
                <Phone className="w-5 h-5 text-rose-500" />
                Emergency Contacts
              </h2>
              <div className="grid md:grid-cols-2 gap-4 text-slate-700">
                <div>
                  <p className="font-semibold">Emergency Services</p>
                  <p className="text-2xl font-bold text-rose-600">999</p>
                </div>
                <div>
                  <p className="font-semibold">NSPCC Helpline</p>
                  <p className="text-2xl font-bold text-rose-600">0808 800 5000</p>
                </div>
                <div>
                  <p className="font-semibold">Childline (for children)</p>
                  <p className="text-2xl font-bold text-rose-600">0800 1111</p>
                </div>
                <div>
                  <p className="font-semibold">Local Authority</p>
                  <p className="text-slate-600">Search for your local children's services</p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Safeguarding;
